import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';

import {
  agents as seedAgents,
  leads,
  showings,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);
const HOUR_MS = 3_600_000;
const TZ_OFFSET_HOURS = -4;
const SLOW_RESPONSE_MIN = 5;

// The only agents that should appear on this page. Keeps admin users or
// renamed records (e.g. a stale "Camila Torres" row) from leaking through.
const ALLOWED_AGENT_IDS = new Set([
  'agent-marco-rivera',
  'agent-ana-castillo',
  'agent-diego-herrera',
  'agent-natalia-ortiz',
  'agent-javier-pena',
  'agent-camila-reyes',
]);

// ── helpers ────────────────────────────────────────────────────────────────

const ROLE_LABEL = {
  team_leader: 'Team Leader',
  agent: 'Agent',
  isa: 'ISA',
};

const ROLE_BADGE = {
  team_leader: 'bg-accent/15 text-accent border border-accent/30',
  agent: 'bg-nurture/15 text-nurture border border-nurture/30',
  isa: 'bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/30',
};

const CLOSED_STATUSES = new Set(['closed_won', 'closed_lost', 'disqualified', 'dead']);

function formatResponseTime(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  const totalSeconds = Math.round(minutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function localDateKey(date) {
  const shifted = new Date(date.getTime() + TZ_OFFSET_HOURS * HOUR_MS);
  return shifted.toISOString().slice(0, 10);
}

// Matches the math used by Analytics → Agent Performance so the two pages
// never disagree about the same agent.
function computeAgentMetrics(agent) {
  const assignedLeads = leads.filter((l) => l.assignedAgentId === agent.id);
  const activeLeads = assignedLeads.filter((l) => !CLOSED_STATUSES.has(l.status)).length;

  const todayKey = localDateKey(NOW);
  const showingsToday = showings.filter(
    (s) =>
      s.agentId === agent.id &&
      s.status !== 'cancelled' &&
      localDateKey(new Date(s.scheduledAt)) === todayKey,
  ).length;

  const showingsCompleted = showings.filter(
    (s) => s.agentId === agent.id && s.status === 'completed',
  ).length;

  const avgResponseMinutes = agent.stats?.avgResponseMinutes ?? null;

  let conversionRate = 0;
  let dealsThisMonth = 0;

  if (agent.role === 'isa') {
    const qualified = agent.stats?.leadsQualifiedMTD ?? 0;
    const booked = agent.stats?.showingsBookedMTD ?? 0;
    conversionRate = qualified > 0 ? (booked / qualified) * 100 : 0;
    dealsThisMonth = booked;
  } else {
    const closings = agent.stats?.closingsYTD ?? 0;
    const denom = closings + activeLeads;
    conversionRate = denom > 0 ? (closings / denom) * 100 : 0;
    // April 2026 → roughly 4 months in, deals "this month" ≈ YTD / 4.
    dealsThisMonth = Math.max(0, Math.round(closings / 4));
  }

  return {
    activeLeads,
    avgResponseMinutes,
    conversionRate,
    showingsToday,
    showingsCompleted,
    dealsThisMonth,
  };
}

// ── subcomponents ──────────────────────────────────────────────────────────

function AgentAvatar({ agent }) {
  return (
    <div
      className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
      style={{ backgroundColor: agent.avatarColor || '#EA580C' }}
      aria-hidden="true"
    >
      {agent.initials}
    </div>
  );
}

function Metric({ label, children }) {
  return (
    <div className="rounded-btn bg-bg-subtle border border-border-card px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-text-primary tabular-nums">
        {children}
      </div>
    </div>
  );
}

function AgentCard({ agent }) {
  const navigate = useNavigate();
  const metrics = useMemo(() => computeAgentMetrics(agent), [agent]);
  const slow =
    Number.isFinite(metrics.avgResponseMinutes) &&
    metrics.avgResponseMinutes > SLOW_RESPONSE_MIN;

  const href = `/leads?agent=${agent.id}`;

  const onCardClick = (e) => {
    // Avoid hijacking clicks on the explicit link/button inside the card.
    if (e.target.closest('a, button')) return;
    navigate(href);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={onKey}
      aria-label={`Open ${agent.name}'s leads`}
      className="card p-6 md:px-7 md:py-6 flex flex-col gap-4 h-full cursor-pointer hover:shadow-card-hover
                 focus:outline-none focus:ring-2 focus:ring-accent/40 transition-shadow"
    >
      {/* Top row */}
      <div className="flex items-start gap-3">
        <AgentAvatar agent={agent} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-text-primary truncate">{agent.name}</div>
          <div className="text-xs text-text-secondary truncate">{agent.title}</div>
        </div>
        <span
          className={`badge whitespace-nowrap ${
            ROLE_BADGE[agent.role] || 'bg-cold/15 text-cold'
          }`}
        >
          {ROLE_LABEL[agent.role] || agent.role}
        </span>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-2">
        <Metric label="Active Leads">{metrics.activeLeads}</Metric>
        <Metric label="Avg Response">
          <span
            className={`inline-flex items-center gap-1 ${slow ? 'text-hot' : ''}`}
          >
            {slow && <AlertCircle size={14} className="text-hot" />}
            {formatResponseTime(metrics.avgResponseMinutes)}
          </span>
        </Metric>
        <Metric label="Conversion Rate">
          {metrics.conversionRate.toFixed(1)}%
        </Metric>
        <Metric label="Showings Today">{metrics.showingsToday}</Metric>
        <Metric label="Showings Completed">{metrics.showingsCompleted}</Metric>
        <Metric label={agent.role === 'isa' ? 'Showings Booked · Month' : 'Deals This Month'}>
          {metrics.dealsThisMonth}
        </Metric>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-1">
        <Link
          to={href}
          className="inline-flex items-center justify-center gap-1.5 w-full rounded-btn
                     border border-accent/60 text-accent bg-accent/5 hover:bg-accent/15
                     hover:border-accent px-3 py-2 text-sm font-medium transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          View Leads
          <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────

export default function Team() {
  // The Team page always sources from the same demo roster as Analytics →
  // Agent Performance. The backend ``/team`` endpoint is not yet seeded
  // with the demo stats (and has returned stray rows in the past, e.g.
  // "Atlacis Admin", "Camila Torres"), so using the seed list directly keeps
  // the page honest and consistent across both dashboards.
  const agents = useMemo(
    () => seedAgents.filter((a) => ALLOWED_AGENT_IDS.has(a.id)),
    [],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="page-title mb-0">Your Team</h1>
          <p className="page-subtitle mb-0 mt-2">
            Every agent, their workload, and their response speed.
          </p>
        </div>
        <span className="badge bg-accent/15 text-accent tabular-nums">
          {agents.length} {agents.length === 1 ? 'agent' : 'agents'}
        </span>
      </header>

      <div
        className="grid gap-6 items-stretch"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}
      >
        {agents.map((agent) => (
          <AgentCard key={agent.id} agent={agent} />
        ))}
      </div>
    </div>
  );
}
